import React, {useEffect} from "react";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import FormField from "../../components/FormComponents/FormField";
import Button from "@mui/material/Button";
import jwtDecode from "jwt-decode";
import User from "../../types/User";
import Container from "@mui/material/Container";
import {DateBirth, Password, PhoneNumber, Snils} from "../../components/FormComponents";
import {checkEmail} from "../../components/FormComponents/Validations";
import {checkPhone} from "../../components/FormComponents/Validations/Validations";
import {GlobalChangeStateObject} from "../../types/Global";
import {checkFormBeforeSubmit} from "../../components/FormComponents/Controller";
import {useSnackbar} from "notistack";
import {Backdrop, TextField, Alert, CircularProgress} from "@mui/material";
import $api from "../../http";
import CircularLoader from "../../components/CircularLoader";
import {isTestUserMode, getTestUserJWTData, testUpdateUserData, isTestToken} from "../../helpers";
import {ConsentEvent, ConsentType} from "../../components/ConsentModal";
import axios from "axios";
import {LARAVEL_API_PATH, LARAVEL_API_TOKEN} from "../../constants/path";
import {getConsentVersions, clearConsent, REQUIRED_CONSENT_TYPES} from "../../helpers";
import useLogout from "../../hooks/useLogout";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";

interface ConsentEventRow {
    consent_datetime: string;
    document_version: string;
    document_name: string;
    consent_type: string;
}

/**
 * Страница с настройками пользователя.
 * @constructor
 */
const SettingsPage: React.FC = () => {

    const token = localStorage.getItem("token");
    const handleLogout = useLogout();
    
    // Получаем данные пользователя в зависимости от режима
    const getUserData = (): User => {
        if (isTestUserMode() && token && isTestToken(token)) {
            return getTestUserJWTData();
        } else {
            //@ts-ignore
            return jwtDecode<User>(token);
        }
    };
    
    const user = getUserData();
    const [userEntity, setUserEntity] = React.useState(user.userEntity);

    const [editFields, setEditFields] = React.useState<boolean>(false)
    const [validFormNew, setValidFormNew] = React.useState({
        phoneNumber: true,
        password: true,
        repeatPassword: false,
        email: true
    });

    const {enqueueSnackbar} = useSnackbar();

    const [newFieldValue, setNewFieldValue] = React.useState({
        email: userEntity.email,
        password: null,
        phoneNumber: userEntity.phoneNumber
    })

    const [requestLoading, setRequestLoading] = React.useState<boolean>(false)
    const [consentEvents, setConsentEvents] = React.useState<ConsentEventRow[]>([]);
    const [consentEventsLoading, setConsentEventsLoading] = React.useState<boolean>(false);
    const [consentEventsError, setConsentEventsError] = React.useState<string | null>(null);

    // Функции для работы с согласиями (перенесены из Cabinet.tsx)
    // Получение IP адреса пользователя
    const getUserIP = async (): Promise<string> => {
        try {
            const response = await axios.get('https://api.ipify.org?format=json');
            return response.data.ip;
        } catch (error) {
            console.error('Error fetching IP:', error);
            return '0.0.0.0';
        }
    };

    // Генерация хеша текста согласия
    const generateTextHash = async (consentType: string): Promise<string> => {
        const text = `${consentType}_${new Date().toISOString().split('T')[0]}`;
        const encoder = new TextEncoder();
        const data = encoder.encode(text);
        const hashBuffer = await crypto.subtle.digest('SHA-256', data);
        const hashArray = Array.from(new Uint8Array(hashBuffer));
        const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
        return hashHex;
    };

    // Получение session_id
    const getSessionId = React.useCallback(async (): Promise<string> => {
        // Проверяем наличие токена в localStorage
        const existingToken = localStorage.getItem('session_token');
        
        if (existingToken) {
            return existingToken;
        }
        
        try {
            // Если токена нет, получаем его с сервера
            const response = await axios.get(`${LARAVEL_API_PATH}api/token`, {
                headers: {
                    'X-API-Token': LARAVEL_API_TOKEN
                }
            });
            
            // Извлекаем токен из ответа
            if (response.data?.success && response.data?.data?.token) {
                const newToken = response.data.data.token;
                // Сохраняем токен в localStorage
                localStorage.setItem('session_token', newToken);
                return newToken;
            } else {
                throw new Error('Invalid token response');
            }
        } catch (error) {
            console.error('Error fetching session token:', error);
            throw error;
        }
    }, []);

    const loadConsentEvents = React.useCallback(async () => {
        setConsentEventsLoading(true);
        setConsentEventsError(null);
        try {
            const sessionId = await getSessionId();
            if (!sessionId) {
                throw new Error('Session ID is not available');
            }

            const response = await axios.get(`${LARAVEL_API_PATH}consent-download/events/search`, {
                params: {
                    session_id: sessionId
                }
            });

            const rows: ConsentEventRow[] = response.data?.rows ?? [];
            setConsentEvents(rows);
        } catch (error: any) {
            console.error('Error fetching consent events:', error);
            const message = error?.response?.data?.message || 'Не удалось загрузить историю согласий';
            setConsentEventsError(message);
            enqueueSnackbar(message, { variant: 'error' });
        } finally {
            setConsentEventsLoading(false);
        }
    }, [enqueueSnackbar, getSessionId]);

    // Отправка события отзыва согласия
    const sendRevokeConsentEvent = async (
        consentType: ConsentType, 
        ip: string, 
        sessionId: string,
        textVersion?: number,
        name?: string
    ) => {
        const textHash = await generateTextHash(consentType);
        const event: ConsentEvent = {
            session_id: sessionId,
            source: 'website',
            consent_type: consentType,
            action: 'revoked',
            consent_text_hash: textHash,
            ip: ip,
            user_agent: navigator.userAgent,
            text_version: textVersion,
            name: name,
        };

        // Если включен тестовый режим, устанавливаем user_id = -999
        if (isTestUserMode()) {
            event.user_id = -999;
        }

        await axios.post(`${LARAVEL_API_PATH}api/consent-events`, event, {
            headers: {
                'X-API-Token': LARAVEL_API_TOKEN
            }
        });
    };

    const handleClickRevokeConsent = async () => {
        if (window.confirm('Отозвать согласие на обработку персональных данных?')) {
            try {
                // Получаем выданные разрешения из localStorage
                const consentVersions = getConsentVersions();
                
                // Проверяем, есть ли выданные согласия
                const grantedConsents = REQUIRED_CONSENT_TYPES.filter(
                    consentType => consentVersions[consentType] !== undefined
                );
                
                if (grantedConsents.length === 0) {
                    enqueueSnackbar('Нет активных согласий для отзыва', { variant: 'info' });
                    return;
                }
                
                // Получаем IP и session_id
                const ip = await getUserIP();
                const sessionId = await getSessionId();
                
                // Формируем ФИО пользователя
                const userFio = `${userEntity.lastName || ''} ${userEntity.firstName || ''} ${userEntity.fatherName || ''}`.trim();
                
                // Отправляем события отзыва для всех выданных согласий
                const revokePromises = grantedConsents.map((consentType) => {
                    const version = consentVersions[consentType];
                    return sendRevokeConsentEvent(consentType, ip, sessionId, version, userFio);
                });
                
                await Promise.all(revokePromises);
                
                // Очищаем данные о согласиях из localStorage
                clearConsent();
                
                enqueueSnackbar('Согласия успешно отозваны', { variant: 'success' });
                void loadConsentEvents();
                
                // Выполняем выход из ЛК после отзыва согласия
                setTimeout(() => {
                    handleLogout();
                }, 1000);
            } catch (error: any) {
                console.error('Error revoking consents:', error);
                enqueueSnackbar(
                    error?.response?.data?.message || 'Ошибка при отзыве согласий',
                    { variant: 'error' }
                );
            }
        }
    };

    const changeNewFieldValue: GlobalChangeStateObject = (field, value) => {
        setNewFieldValue({
            ...newFieldValue,
            [field]: value
        })
    }

    const newSaveUser = async () => {
        setEditFields(false)
        setRequestLoading(true)
        
        try {
            if (isTestUserMode() && token && isTestToken(token)) {
                // Тестовый режим - имитируем обновление данных
                const response = await testUpdateUserData(newFieldValue);
                localStorage.setItem("token", response.token);
                enqueueSnackbar("Данные успешно изменены (тестовый режим)", {variant: "success"});
            } else {
                // Обычный режим - запрос к backend
                const response = await $api.put(`auth/updateUserData`,
                    newFieldValue,
                    {
                        headers: {'Content-Type': 'application/json'}
                    });
                
                if (response.status === 200) {
                    localStorage.setItem("token", response.data.token);
                    enqueueSnackbar("Данные успешно изменены", {variant: "success"});
                }
            }
        } catch (error: any) {
            enqueueSnackbar(error.response?.data || "Ошибка при обновлении данных", {variant: "error"});
            console.error(error);
            rollback();
        } finally {
            setRequestLoading(false);
        }
    }

    const handleClick = () => {
        if (editFields) {
            newSaveUser()
        }
        setEditFields(!editFields)
    }

    const rollback = () => {
        const user = getUserData();
        const oldUserEntity = user.userEntity;
        setUserEntity(oldUserEntity);
    }

    useEffect(() => {
        document.title = "Настройки";
    }, [])

    useEffect(() => {
        void loadConsentEvents();
    }, [loadConsentEvents]);

    return (
        <Container component="main" maxWidth="sm">
            <Backdrop
                sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
                open={requestLoading}
            >
                <CircularLoader/>
            </Backdrop>
            <Box component="form" className={'settings__page'} noValidate>
                <Grid container spacing={2} alignItems={"center"}>
                    <Grid item xs={12} sm={12}>
                        <Typography align={"center"} component="h1" variant="h5">
                            {"Настройки"}
                        </Typography>
                    </Grid>
                    {isTestUserMode() && token && isTestToken(token) && (
                        <Grid item xs={12} sm={12}>
                            <Alert severity="warning" sx={{ mb: 2 }}>
                                <strong>ТЕСТОВЫЙ РЕЖИМ</strong><br />
                                Данные пользователя загружены из тестовых данных.<br />
                                Изменения не сохраняются на сервере.
                            </Alert>
                        </Grid>
                    )}
                    <Grid item xs={12} sm={12}>
                        <FormField
                            label={"ФИО"}
                            name={"lastName"}
                            defaultValue={`${userEntity.lastName} ${userEntity.firstName} ${userEntity.fatherName ?? ""}`}
                            disabled={true}
                        />
                    </Grid>
                    {!(isTestUserMode() && token && isTestToken(token)) && (
                        <Grid item xs={12} sm={12}>
                            <DateBirth
                                defaultValue={userEntity.birthDay}
                                readOnly={true}
                                disabled={true}
                            />
                        </Grid>
                    )}
                    {userEntity.snils ?
                        <Grid item xs={12} sm={12}>
                            <Snils
                                required={false}
                                readOnly={true}
                                defaultValue={userEntity.snils}
                            />
                        </Grid>
                    : <></>}
                    <Grid item xs={12} sm={12}>
                        <PhoneNumber
                            disabled={!editFields}
                            readOnly={!editFields}
                            edit={editFields}
                            changeState={changeNewFieldValue}
                            defaultValue={newFieldValue.phoneNumber ?? userEntity.phoneNumber}
                            validForm={validFormNew}
                            setValidForm={setValidFormNew}
                            additionalCheck={checkPhone}
                            rollbackEnabled={true}
                        />
                    </Grid>
                    <Grid item xs={12} sm={12}>
                        <FormField
                            label={"Логин"}
                            name={"login"}
                            defaultValue={userEntity.login}
                            readOnly={true}
                            disabled={true}
                        />
                    </Grid>
                    <Grid item xs={12} sm={12}>
                        {editFields ?
                            <Password
                                edit={editFields}
                                readOnly={!editFields}
                                changeState={changeNewFieldValue}
                                label={"Пароль"}
                                validForm={validFormNew}
                                setValidForm={setValidFormNew}
                                name={"password"}
                            /> :
                            <TextField
                                fullWidth
                                id={"fakePassword"}
                                label={"Пароль"}
                                name={"fakePassword"}
                                value={"fakePassword"}
                                color="secondary"
                                type={'password'}
                                disabled={!editFields}
                            />}
                    </Grid>
                    {editFields ? (
                        <Grid item xs={12} sm={12}>
                            <Password
                                edit={editFields}
                                label={"Повторите пароль"}
                                validForm={validFormNew}
                                setValidForm={setValidFormNew}
                                name={"repeatPassword"}
                                repeatPassword
                            />
                        </Grid>
                    ) : (
                        <></>
                    )}

                    <Grid item xs={12} sm={12}>
                        <FormField
                            edit={editFields}
                            defaultValue={newFieldValue.email ?? userEntity.email}
                            validForm={validFormNew}
                            setValidForm={setValidFormNew}
                            changeState={changeNewFieldValue}
                            label={"Адрес электронной почты"}
                            additionalCheck={checkEmail}
                            name={"email"}
                            rollbackEnabled={true}
                            disabled={!editFields}
                        />
                    </Grid>
                    <Grid item xs={12} sm={12}>
                        <Button
                            fullWidth
                            disabled={editFields ? checkFormBeforeSubmit(validFormNew) : false}
                            onClick={handleClick}
                            variant="contained"
                            color={"secondary"}
                        >
                            {editFields ? "Сохранить" : "Изменить"}
                        </Button>
                    </Grid>
                    <Grid item xs={12} sm={12}>
                        <Button
                            fullWidth
                            variant="outlined"
                            color="warning"
                            onClick={handleClickRevokeConsent}
                        >
                            Отозвать согласие
                        </Button>
                    </Grid>
                    <Grid item xs={12} sm={12}>
                        <Box sx={{ mt: 2 }}>
                            <Typography component="h2" variant="h6" gutterBottom>
                                Просмотр согласий
                            </Typography>
                            {consentEventsLoading ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                    <CircularProgress color="secondary" size={32}/>
                                </Box>
                            ) : consentEventsError ? (
                                <Alert severity="error">{consentEventsError}</Alert>
                            ) : consentEvents.length > 0 ? (
                                <TableContainer component={Paper}>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Дата и время</TableCell>
                                                <TableCell>Версия документа</TableCell>
                                                <TableCell>Название документа</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {consentEvents.map((row, index) => (
                                                <TableRow key={`${row.consent_datetime}_${row.document_name}_${index}`}>
                                                    <TableCell>{row.consent_datetime}</TableCell>
                                                    <TableCell>{row.document_version}</TableCell>
                                                    <TableCell>
                                                        <a 
                                                            href={`${LARAVEL_API_PATH}consent-download/${row.consent_type}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            style={{ color: 'inherit', textDecoration: 'underline', cursor: 'pointer' }}
                                                        >
                                                            {row.document_name}
                                                        </a>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            ) : (
                                <Typography color="text.secondary">
                                    Нет записей о согласиях
                                </Typography>
                            )}
                        </Box>
                    </Grid>
                </Grid>
            </Box>
        </Container>
    )
}

export default React.memo(SettingsPage);
